import { h, Component, ComponentType } from "preact";

import { withMultiSelectGroupContext, WithMultiSelectGroupContextProps } from "./MultiSelectGroupContext";
import { MultiSelectItem, MultiSelectItemState, SelectionUpdate } from "./types";
import { BoundsScrollProps, ComputedBounds, getBoundsForNode } from "./utils/bounds";

// These are required to be passed to the wrapped component by the consumer
interface MultiSelectItemRequiredProps<T> extends WithMultiSelectGroupContextProps {
    selectableId: T;
    isSelected?: boolean;
}

// These are injected to wrapped component by `withMultiSelectItem`
interface MultiSelectItemInjectedProps {
    itemSelecting?: boolean;
    itemSelected?: boolean;
    selectableRef?: (node: HTMLElement | null) => void;
    toggleSelection?: () => void;
}

// Components that use `withMultiSelectItem` should extend from this type when declaring props
type WithMultiSelectItemProps<T = string> = MultiSelectItemRequiredProps<T> & MultiSelectItemInjectedProps;

// This is the props of returned component from `withMultiSelectItem`
type MultiSelectItemProps<P> = Omit<P, keyof MultiSelectItemInjectedProps>;

/**
 * P is the type of own props of the wrapped component
 * T is the type of the selectableId
 */
function withMultiSelectItem<P extends MultiSelectItemRequiredProps<T>, T>(
    WrappedComponent: ComponentType<P>
) {
    return withMultiSelectGroupContext(
        class extends Component<MultiSelectItemProps<P>, MultiSelectItemState> implements MultiSelectItem<T> {
            node: HTMLElement | null = null;
            bounds: ComputedBounds | null = null;
            deselected = false;

            override state: MultiSelectItemState = {
                isSelecting: false,
                isSelected: !!this.props.isSelected
            };

            get selectionState() {
                const { isSelecting, isSelected } = this.state;
                return { isSelecting, isSelected };
            }

            get selectableId() {
                return this.props.selectableId;
            }

            get multiSelectGroupContext() {
                if (!this.props.multiSelectGroupContext) {
                    throw new Error("Components wrapped with 'withMultiSelectItem' should be used inside 'MultiSelectGroup' component");
                }

                return this.props.multiSelectGroupContext!;
            }

            public updateSelectionState = (selectionUpdate: SelectionUpdate) => {
                this.setState({
                    ...selectionUpdate
                });
            };

            public updateBounds = (containerScroll: BoundsScrollProps) => {
                this.bounds = getBoundsForNode(this.node!, containerScroll);
            };

            handleToggleSelection = () => {
                this.multiSelectGroupContext.toggleSelection(this);
            };

            handleSelectableRef = (ref: HTMLElement | null) => {
                this.node = ref;
            };

            override componentDidMount() {
                this.multiSelectGroupContext.register(this);
            }

            override componentDidUpdate(prevProps: P) {
                if (prevProps.selectableId !== this.props.selectableId) {
                    this.multiSelectGroupContext.updateRegistry(this);
                }
            }

            override componentWillUnmount() {
                this.multiSelectGroupContext.unregister(this);
            }

            render() {
                const { isSelecting, isSelected } = this.state;

                return (
                    // Make sure to pass all props of the `MultiSelectItemInjectedProps` here
                    <WrappedComponent
                        {...(this.props as P)}
                        itemSelecting={isSelecting}
                        itemSelected={isSelected}
                        selectableRef={this.handleSelectableRef}
                        toggleSelection={this.handleToggleSelection}/>
                );
            }
        }
    );
}

export {
    withMultiSelectItem,
    WithMultiSelectItemProps
};
